import type {ReactElement} from 'react';
import {SkeletonBrick} from '@Components/skeleton-brick/skeleton-brick';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {skeletonLoaderContainerQuery} from '@Components/design-selector/design-selector.types';
import type {DesignSelectorSkeletonLoaderProps} from './design-selector-skeleton-loader.types';
import styles from './design-selector-skeleton-loader.module.scss';

export function DesignSelectorSkeletonLoader({isVisible, containerRef}: DesignSelectorSkeletonLoaderProps): ReactElement | null {
  const skeletonParams = useContainerQuery(containerRef, skeletonLoaderContainerQuery);

  if (!isVisible) {
    return null;
  }

  const getFolderLoader = (): ReactElement => {
    let folderLoaderCount = 0;
    let rightMarginString = '';

    if (skeletonParams.xxsmall) {
      folderLoaderCount = 1;
    } else if (skeletonParams.xsmall) {
      folderLoaderCount = 2;
      rightMarginString = 'spacing-m-r-3';
    } else if (skeletonParams.small) {
      folderLoaderCount = 3;
      rightMarginString = 'spacing-m-r-4';
    } else if (skeletonParams.medium || skeletonParams.large) {
      folderLoaderCount = 2;
      rightMarginString = 'spacing-m-r-5';
    } else if (skeletonParams.xlarge || skeletonParams.xxlarge) {
      folderLoaderCount = 3;
      rightMarginString = 'spacing-m-r-5';
    } else if (skeletonParams.xxxlarge) {
      folderLoaderCount = 4;
      rightMarginString = 'spacing-m-r-6';
    }
    const skeletonArray: ReactElement[] = [];
    for (let i = 0; i < folderLoaderCount; i++) {
      const isLastFolder = i === folderLoaderCount - 1;
      skeletonArray.push(<SkeletonBrick isPulsating className={`radius-2 ${isLastFolder ? '' : rightMarginString}`} height={56} key={`design-selector-skeleton-${i}`} />);
    }
    return <div className="spacing-m-b-3 flex-h-row">{skeletonArray}</div>;
  };

  const getPosterLoader = (): ReactElement => {
    let posterLoaderCount = 0;
    let rightMarginString = '';
    if (skeletonParams.xxsmall) {
      posterLoaderCount = 1;
    } else if (skeletonParams.xsmall || skeletonParams.small) {
      posterLoaderCount = 3;
      rightMarginString = 'spacing-m-r-5';
    } else if (skeletonParams.medium) {
      posterLoaderCount = 2;
      rightMarginString = 'spacing-m-r-6';
    } else if (skeletonParams.large || skeletonParams.xlarge) {
      posterLoaderCount = 3;
      rightMarginString = 'spacing-m-r-6';
    } else if (skeletonParams.xxlarge || skeletonParams.xxxlarge) {
      posterLoaderCount = 4;
      rightMarginString = 'spacing-m-r-5';
    }

    const skeletonArray: ReactElement[] = [];
    for (let i = 0; i < posterLoaderCount; i++) {
      const isLastPoster = i === posterLoaderCount - 1;

      skeletonArray.push(
        <div className={isLastPoster ? '' : rightMarginString} style={{width: '100%'}} key={`design-selector-poster-skeleton-${i}`}>
          <SkeletonBrick isPulsating className="spacing-m-b-4" height={148} />
          <SkeletonBrick isPulsating height={20} width={136} />
        </div>
      );
    }
    return <div className="flex-h-row">{skeletonArray}</div>;
  };

  return (
    <div className={styles.loaderWrapperMystuff}>
      <div className={styles.designSelectorLoaderDesktop}>
        {getFolderLoader()}
        <div className={styles.viewMoreButtonSkeletonContainer}>
          <SkeletonBrick isPulsating className={`radius-4 ${styles.viewMoreButtonSkeleton}`} width={80} height={20} />
        </div>
        {getPosterLoader()}
      </div>
    </div>
  );
}
